/** Admin — Email Audit: who actually received what, and who counts as staff for email fan-out right now. */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, MailSearch, Loader2, ShieldCheck, UserX, AlertTriangle, Search } from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const KIND = {
  active_staff: 'bg-emerald-100 text-emerald-800', installer: 'bg-sky-100 text-sky-800', no_account: 'bg-gray-100 text-gray-700',
  deleted_staff: 'bg-rose-100 text-rose-800', deleted: 'bg-rose-50 text-rose-700',
};

function DetailList({ detail, compact }) {
  if (!detail?.length) return <p className="text-xs text-gray-500 italic py-1">No individual emails in this window.</p>;
  if (compact) {
    return (
      <ul className="text-xs divide-y">{detail.map((d, i) => (
        <li key={i} className="py-1.5">
          <div className="flex items-center gap-2"><span className={`font-bold ${d.ok ? 'text-emerald-700' : 'text-rose-700'}`}>{d.ok ? 'sent' : 'FAILED'}</span><span className="text-gray-500">{fmt(d.at)}</span><span className="text-gray-400 truncate ml-auto">{d.context}</span></div>
          <div className="text-gray-900 break-words">{d.subject}</div>
          {!d.ok && d.error && <div className="text-rose-700 break-words">{d.error}</div>}
        </li>))}
      </ul>
    );
  }
  return <ul className="text-xs divide-y">{detail.map((d, i) => <li key={i} className="py-1 flex gap-3"><span className="text-gray-500 w-32 shrink-0">{fmt(d.at)}</span><span className={`w-16 shrink-0 font-bold ${d.ok ? 'text-emerald-700' : 'text-rose-700'}`}>{d.ok ? 'sent' : 'FAILED'}</span><span className="text-gray-500 w-40 shrink-0 truncate">{d.context}</span><span className="text-gray-900 truncate">{d.subject}</span>{!d.ok && d.error && <span className="text-rose-700 truncate">{d.error}</span>}</li>)}</ul>;
}

function StaffPanel({ data }) {
  if (!data) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card className="p-4" data-testid="active-staff-panel">
        <h2 className="font-bold text-gray-900 flex items-center gap-2 mb-2"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Staff-wide emails go to these {data.active_staff.length} people</h2>
        <ul className="text-sm divide-y">
          {data.active_staff.map((u) => <li key={u.user_id} className="py-1.5 flex flex-wrap sm:flex-nowrap items-baseline justify-between gap-x-2" data-testid={`active-staff-${u.user_id}`}><span className="font-medium text-gray-900 truncate">{u.name}</span><span className="text-gray-500 truncate basis-full sm:basis-auto order-last sm:order-none text-xs sm:text-sm">{u.email}</span><span className="text-[10px] uppercase font-bold text-gray-400 shrink-0">{u.role}</span></li>)}
        </ul>
      </Card>
      <Card className="p-4" data-testid="deleted-staff-panel">
        <h2 className="font-bold text-gray-900 flex items-center gap-2 mb-2"><UserX className="w-4 h-4 text-rose-600" /> Removed staff — excluded from every list</h2>
        {data.excluded_deleted_staff.length === 0 ? <p className="text-sm text-gray-500 italic">None.</p> : (
          <ul className="text-sm divide-y">
            {data.excluded_deleted_staff.map((u) => <li key={u.user_id} className="py-1.5 flex flex-wrap sm:flex-nowrap items-baseline justify-between gap-x-2"><span className="font-medium text-gray-700 truncate">{u.name}</span><span className="text-gray-500 truncate basis-full sm:basis-auto order-last sm:order-none text-xs sm:text-sm">{u.email}</span><span className="text-[10px] text-gray-400 shrink-0">removed {u.deleted_at?.slice(0, 10)}</span></li>)}
          </ul>
        )}
        <p className="text-[11px] text-gray-500 mt-2">If a removed person still gets email, it's coming from outside the app (a forwarding rule on a group mailbox such as orders@) or they hold a second, non-staff account — check the recipient list below.</p>
      </Card>
    </div>
  );
}

export default function EmailAudit() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [q, setQ] = useState('');
  const [audit, setAudit] = useState(null);
  const [staff, setStaff] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { if (user && user.role !== 'admin') navigate('/staff', { replace: true }); }, [user, navigate]);

  const load = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${API_URL}/api/admin/email-audit?${new URLSearchParams({ days, q })}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/admin/email-audit/staff-recipients`, { credentials: 'include' }),
      ]);
      if (!r1.ok) throw new Error('Could not load email audit');
      setAudit(await r1.json());
      if (r2.ok) setStaff(await r2.json());
    } catch (e) { toast.error(e.message); }
  }, [days, q]);
  useEffect(() => { if (user?.role === 'admin') load(); }, [user, load]);

  const flagged = audit?.recipients.filter((r) => r.account.kind === 'deleted_staff') || [];
  const detail = expanded && audit?.recent.filter((r) => (r.to || []).some((t) => t.toLowerCase() === expanded));

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white">
      <div className="border-b sticky top-0 z-20 shadow-sm bg-[#27292E] text-white">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3">
          <Link to="/staff" className="text-white/80 hover:text-white" data-testid="email-audit-back"><ArrowLeft className="w-5 h-5" /></Link>
          <Link to="/" className="text-white/80 hover:text-white hidden sm:inline-flex"><Home className="w-5 h-5" /></Link>
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center"><MailSearch className="w-4 h-4" /></div>
          <h1 className="text-base sm:text-lg font-bold truncate flex-1">Email Audit — who got what</h1>
          <Link to="/staff/message-centre" className="hidden md:inline text-xs text-white/80 hover:text-white underline">Message Centre</Link>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4">
        <StaffPanel data={staff} />
        {flagged.length > 0 && (
          <Card className="p-4 border-rose-200 bg-rose-50" data-testid="flagged-recipients">
            <h2 className="font-bold text-rose-900 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Removed staff who still received email in the last {days} days</h2>
            <ul className="text-sm mt-2 space-y-1">{flagged.map((r) => <li key={r.email}><strong>{r.account.name}</strong> ({r.email}) — {r.count} email{r.count === 1 ? '' : 's'}, last {fmt(r.last_at)} · {r.contexts.map(([c, n]) => `${c} ×${n}`).join(', ')}</li>)}</ul>
          </Card>
        )}
        <Card className="p-3 flex flex-wrap items-center gap-2">
          {[7, 30, 90].map((d) => <button key={d} type="button" onClick={() => setDays(d)} className={`text-xs font-semibold px-3 py-1 rounded-full border ${days === d ? 'bg-[#27292E] text-white border-[#27292E]' : 'bg-white text-gray-600 border-gray-200'}`} data-testid={`audit-days-${d}`}>Last {d} days</button>)}
          <div className="relative w-full sm:w-auto sm:ml-auto sm:min-w-[240px]"><Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search address, subject or type…" className="h-8 pl-7 text-xs bg-white" data-testid="audit-search" /></div>
        </Card>
        {!audit ? <div className="py-10 flex justify-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /></div> : (
          <Card className="p-0 overflow-hidden" data-testid="recipients-table">
            <div className="px-4 py-3 border-b bg-gray-50 text-sm"><strong>{audit.total}</strong> emails sent since {fmt(audit.since)} to <strong>{audit.recipients.length}</strong> addresses. <span className="hidden sm:inline">Click a row</span><span className="sm:hidden">Tap a person</span> to see the individual emails.</div>
            <ul className="md:hidden divide-y" data-testid="recipients-cards">
              {audit.recipients.map((r) => (
                <li key={r.email} data-testid={`recipient-card-${r.email}`}>
                  <button type="button" onClick={() => setExpanded(expanded === r.email ? null : r.email)} className="w-full text-left px-4 py-3 active:bg-gray-50">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0"><div className="font-medium text-gray-900 break-all">{r.email}</div>{r.account.name && <div className="text-[11px] text-gray-500">{r.account.name}</div>}</div>
                      <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${KIND[r.account.kind]}`}>{r.account.label}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
                      <span><strong className="text-gray-900 tabular-nums">{r.count}</strong> sent</span>
                      <span className={r.failed ? 'text-rose-700 font-semibold' : ''}>{r.failed || 0} failed</span>
                      <span className="text-gray-500">last {fmt(r.last_at)}</span>
                    </div>
                    <div className="mt-1 text-[11px] text-gray-500 truncate">{r.contexts.slice(0, 3).map(([c, n]) => `${c} ×${n}`).join(' · ')}{r.contexts.length > 3 ? ' …' : ''}</div>
                  </button>
                  {expanded === r.email && <div className="bg-gray-50 px-4 py-2"><DetailList detail={detail} compact /></div>}
                </li>
              ))}
            </ul>
            <table className="hidden md:table w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wider text-gray-500 border-b"><tr><th className="text-left px-4 py-2">Recipient</th><th className="text-left px-3 py-2">Account</th><th className="text-right px-3 py-2">Emails</th><th className="text-right px-3 py-2">Failed</th><th className="text-left px-3 py-2">Last</th><th className="text-left px-4 py-2">Types</th></tr></thead>
              <tbody className="divide-y">
                {audit.recipients.map((r) => (
                  <React.Fragment key={r.email}>
                    <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setExpanded(expanded === r.email ? null : r.email)} data-testid={`recipient-row-${r.email}`}>
                      <td className="px-4 py-2"><div className="font-medium text-gray-900">{r.email}</div>{r.account.name && <div className="text-[11px] text-gray-500">{r.account.name}</div>}</td>
                      <td className="px-3 py-2"><span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${KIND[r.account.kind]}`}>{r.account.label}</span></td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold">{r.count}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${r.failed ? 'text-rose-700 font-semibold' : 'text-gray-400'}`}>{r.failed || '—'}</td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{fmt(r.last_at)}</td>
                      <td className="px-4 py-2 text-[11px] text-gray-600">{r.contexts.slice(0, 4).map(([c, n]) => `${c} ×${n}`).join(' · ')}{r.contexts.length > 4 ? ' …' : ''}</td>
                    </tr>
                    {expanded === r.email && (
                      <tr><td colSpan={6} className="bg-gray-50 px-4 py-2"><DetailList detail={detail} /></td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </div>
  );
}
